/**
 * 实体序列化器
 *
 * 负责实体的序列化和反序列化操作
 */
import { Entity } from '../Entity';
import { ComponentSerializer } from './ComponentSerializer';
import { HierarchyComponent } from '../Components/HierarchyComponent';
/**
 * 实体序列化器类
 */
export class EntitySerializer {
    /**
     * 序列化单个实体
     *
     * @param entity 要序列化的实体
     * @param includeChildren 是否包含子实体（默认true）
     * @param hierarchySystem 层级系统（可选，用于获取层级信息）
     * @returns 序列化后的实体数据
     */
    static serialize(entity, includeChildren = true, hierarchySystem) {
        const serializedComponents = ComponentSerializer.serializeComponents(Array.from(entity.components));
        const serializedEntity = {
            id: entity.id,
            guid: entity.persistentId,
            name: entity.name,
            tag: entity.tag,
            active: entity.active,
            enabled: entity.enabled,
            updateOrder: entity.updateOrder,
            components: serializedComponents,
            children: []
        };
        // 通过 HierarchyComponent 获取层级信息
        const hierarchy = entity.getComponent(HierarchyComponent);
        if (hierarchy?.parentId !== null && hierarchy?.parentId !== undefined) {
            serializedEntity.parentId = hierarchy.parentId;
        }
        // 序列化子实体
        // 直接使用 HierarchyComponent.childIds 获取子实体
        if (includeChildren && hierarchy && hierarchy.childIds.length > 0) {
            // 获取场景引用：优先从 hierarchySystem，否则从 entity.scene
            const scene = hierarchySystem?.scene ?? entity.scene;
            if (scene) {
                for (const childId of hierarchy.childIds) {
                    const child = scene.findEntityById(childId);
                    if (child) {
                        serializedEntity.children.push(this.serialize(child, true, hierarchySystem));
                    }
                }
            }
        }
        return serializedEntity;
    }
    /**
     * 反序列化实体
     *
     * Deserialize an entity from serialized data.
     *
     * @param serializedEntity 序列化的实体数据
     * @param componentRegistry 组件类型注册表
     * @param idGenerator 实体ID生成器（用于生成新ID或保持原ID）
     * @param preserveIds 是否保持原始ID（默认false）
     * @param scene 目标场景（可选，用于设置entity.scene以支持添加组件）
     * @param hierarchySystem 层级系统（可选，用于建立层级关系）
     * @param allEntities 所有实体的映射（可选，用于收集所有实体）
     * @param context 序列化上下文（可选，用于解析 EntityRef）
     * @returns 反序列化后的实体
     */
    static deserialize(serializedEntity, componentRegistry, idGenerator, preserveIds = false, scene, hierarchySystem, allEntities, context) {
        // 创建实体（使用原始ID或新生成的ID，保留原始 GUID）
        const entityId = preserveIds ? serializedEntity.id : idGenerator();
        const entity = new Entity(serializedEntity.name, entityId, serializedEntity.guid);
        // 将实体添加到收集 Map 中（用于后续添加到场景）
        allEntities?.set(entity.id, entity);
        // 注册实体到序列化上下文（用于后续解析 EntityRef）
        if (context) {
            context.registerEntity(entity, serializedEntity.id, serializedEntity.guid);
        }
        // 如果提供了scene，先设置entity.scene以支持添加组件
        if (scene) {
            entity.scene = scene;
        }
        // 恢复实体属性
        entity.tag = serializedEntity.tag;
        entity.active = serializedEntity.active;
        entity.enabled = serializedEntity.enabled;
        entity.updateOrder = serializedEntity.updateOrder;
        // 反序列化组件（传入 context 以支持 EntityRef 解析）
        const components = ComponentSerializer.deserializeComponents(serializedEntity.components, componentRegistry, context);
        for (const component of components) {
            entity.addComponent(component);
        }
        // 重要：清除 HierarchyComponent 中的旧 ID
        // 当 preserveIds=false 时，序列化的 parentId 和 childIds 是旧 ID，需要重新建立
        // 通过 hierarchySystem.setParent 会正确设置新的 ID
        const hierarchy = entity.getComponent(HierarchyComponent);
        if (hierarchy) {
            hierarchy.parentId = null;
            hierarchy.childIds = [];
        }
        // 反序列化子实体并建立层级关系
        for (const childData of serializedEntity.children) {
            const childEntity = this.deserialize(childData, componentRegistry, idGenerator, preserveIds, scene, hierarchySystem, allEntities, context);
            // 使用 HierarchySystem 建立层级关系
            hierarchySystem?.setParent(childEntity, entity);
        }
        return entity;
    }
    /**
     * 批量序列化实体
     *
     * @param entities 实体数组
     * @param includeChildren 是否包含子实体
     * @param hierarchySystem 层级系统（可选，用于获取层级信息）
     * @returns 序列化后的实体数据数组
     */
    static serializeEntities(entities, includeChildren = true, hierarchySystem) {
        const result = [];
        for (const entity of entities) {
            // 只序列化顶层实体（没有父实体的实体）
            // 子实体会在父实体序列化时一并处理
            const hierarchy = entity.getComponent(HierarchyComponent);
            const bHasParent = hierarchy?.parentId !== null && hierarchy?.parentId !== undefined;
            if (!bHasParent || !includeChildren) {
                result.push(this.serialize(entity, includeChildren, hierarchySystem));
            }
        }
        return result;
    }
    /**
     * 批量反序列化实体
     *
     * Deserialize multiple entities from serialized data.
     *
     * @param serializedEntities 序列化的实体数据数组
     * @param componentRegistry 组件类型注册表
     * @param idGenerator 实体ID生成器
     * @param preserveIds 是否保持原始ID
     * @param scene 目标场景（可选，用于设置entity.scene以支持添加组件）
     * @param hierarchySystem 层级系统（可选，用于建立层级关系）
     * @param context 序列化上下文（可选，用于解析 EntityRef）
     * @returns 反序列化后的实体数组
     */
    static deserializeEntities(serializedEntities, componentRegistry, idGenerator, preserveIds = false, scene, hierarchySystem, context) {
        const rootEntities = [];
        const allEntities = new Map();
        for (const serialized of serializedEntities) {
            const entity = this.deserialize(serialized, componentRegistry, idGenerator, preserveIds, scene, hierarchySystem, allEntities, context);
            rootEntities.push(entity);
        }
        return { rootEntities, allEntities };
    }
    /**
     * 创建实体的深拷贝
     *
     * @param entity 要拷贝的实体
     * @param componentRegistry 组件类型注册表
     * @param idGenerator ID生成器
     * @returns 拷贝后的新实体
     */
    static clone(entity, componentRegistry, idGenerator) {
        const serialized = this.serialize(entity, true);
        return this.deserialize(serialized, componentRegistry, idGenerator, false);
    }
}
//# sourceMappingURL=EntitySerializer.js.map